<template>
  <div>
    <apex-chart
      v-if="locations.length > 0"
      type="bar"
      :height="height"
      :options="chartOptions"
      :series="series"
    />
  </div>
</template>

<script>
// Import node packages
import ApexChart from 'vue-apexcharts'
import merge from 'lodash/merge'

// Import default configuration option for chart
import apexChartBaseOptions from '@/json/apexChartBaseOptions.js'

// Import helper function
import dataHelper from "@/helpers/dataHelper"

// Export the SFC
export default {
  // Name of the component
  name: "CityTiers",

  // Register the components
  components: {
    ApexChart
  },

  // Accept incoming data from parent
  props: {
    data: {
      type: Array,
      required: true
    },

    height: {
      type: Number,
      default: 284
    }
  },

  // Define local data variables
  data: () => ({
    locations: []
  }),

  // Readonly computable variables
  computed: {
    /**
     * Get the chart series data
     *
     * @returns {Array}
     */
    series() {
      const tiers = {
        "1": {
          x: "Tier 1",
          y: 0
        },
        "2": {
          x: "Tier 2",
          y: 0
        },
        "3": {
          x: "Tier 3",
          y: 0
        }
      }

      // Read the header row
      const header = this.locations[0]

      // Get the index mapping for keys
      const indexMapping = Object.values(header).reduce((acc, key, index) => {
        acc[key] = index
        return acc
      }, {})

      for (const item of this.data) {
        const name = item.name.split(",")[0].trim()

        // Find the tier based on the name
        const object = this.locations.find((location) => {
          return location[indexMapping["name"]].trim() === name
        })

        // If found
        if (object && object[indexMapping["city_tier"]]) {
          const tier = object[indexMapping["city_tier"]].trim()

          // If the tier is valid
          if (tiers[tier]) {
            tiers[tier].y += item.influencers_count
          }
        }
      }

      const data = Object.values(tiers)
      const total = data.reduce((sum, item) => sum + item.y, 0)

      return [
        {
          data: data.map((item) => ({
            x: item.x,
            y: (item.y / total) * 100
          }))
        }
      ]
    },

    /**
     * Get the chart configuration options
     *
     * @returns {Object}
     */
    chartOptions() {
      return merge(apexChartBaseOptions(), {
        stroke: {
          width: 5,
        },
        colors: ["#2196F3", "#ffca28"],
        dataLabels: {
          enabled: true,
          textAnchor: "start",
          offsetY: -12,
          formatter(value) {
            return value ? numeral.fPercent(value) : "";
          },
          style: {
            colors: ["#f05d56"],
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "15%",
            borderRadius: 2,
            padding: 10,

            dataLabels: {
              enabled: true,
              position: "right",
            },
          },
        },
        yaxis: {
          type: "category",
        },
        tooltip: {
          enabled: false,
          marker: { show: false },
        },
        xaxis: {
          type: "numeric",
          labels: {
            format: "asd",
          },
          title: {
            text: "Percent",
          },
        },
      })
    }
  },

  /**
   * As soon as the component data is ready
   *
   * @returns {void}
   */
  async created() {
    // Fetch and store interests in the store
    this.locations = await dataHelper.loadData("locations.json")
  },

  /**
   * Before the component is destroyed
   *
   * @returns {void}
   */
  beforeDestroy() {
    // Clear the locations array
    dataHelper.unloadData("locations.json")
    this.locations = []
  }
}
</script>
